<template>
  <div class="modal video-preview-modal" :class="{ 'is-active': show }">
    <div class="modal-background" @click="closeModal"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">{{ asset?.name || 'Preview' }}</p>
        <button class="delete" aria-label="close" @click="closeModal"></button>
      </header>
      <section class="modal-card-body">
        <div v-if="loading" class="has-text-centered py-6">
          <span class="icon is-large">
            <i class="fas fa-spinner fa-pulse fa-3x"></i>
          </span>
        </div>
        
        <div v-else-if="error" class="notification is-danger">
          <p>{{ error }}</p>
        </div>
        
        <template v-else>
          <VideoPlayer
            v-if="isVideo && videoUrl"
            :src="videoUrl"
            :poster-url="posterUrl"
            :controls="true"
            :autoplay="false"
            :muted="true"
            :preload="'metadata'"
            @error="handleVideoError"
            @play="onVideoPlay"
            @pause="onVideoPause"
            ref="videoPlayer"
            class="video-preview-player"
          />
          <img
            v-else-if="!isVideo && assetUrl"
            :src="assetUrl"
            :alt="asset?.name || 'Preview'"
            class="image-preview"
          />
        </template>
      </section>
      
      <footer class="modal-card-foot">
        <div class="level is-mobile is-fullwidth">
          <div class="level-left">
            <button 
              v-if="showNavigation && canNavigatePrev"
              class="button is-primary" 
              @click="$emit('navigate', -1)"
            >
              <span class="icon">
                <font-awesome-icon :icon="['fas', 'chevron-left']" />
              </span>
              <span>Previous</span>
            </button>
          </div>
          
          <div class="level-item" v-if="showSelectButton || hasTranscript">
            <button
              v-if="hasTranscript"
              class="button"
              @click="showTranscript = true"
            >
              <span class="icon">
                <font-awesome-icon :icon="['fal', 'file-text']" />
              </span>
              <span>Transcript</span>
            </button>

            <button
              v-if="showSelectButton"
              class="button is-primary"
              @click="$emit('select', asset)"
            >
              <span class="icon">
                <font-awesome-icon :icon="['fas', isSelected ? 'check-square' : 'square']" />
              </span>
              <span>{{ isSelected ? 'Selected' : 'Select Asset' }}</span>
            </button>
          </div>
          
          <div class="level-right">
            <button 
              v-if="showNavigation && canNavigateNext"
              class="button is-primary" 
              @click="$emit('navigate', 1)"
            >
              <span>Next</span>
              <span class="icon">
                <font-awesome-icon :icon="['fas', 'chevron-right']" />
              </span>
            </button>
          </div>
        </div>
      </footer>
    </div>

    <!-- Read-only transcript view with copy + download (same drawer as AssetDetail). -->
    <TranscriptDrawer
      v-model="showTranscript"
      :vtt="asset?.transcript || ''"
      :filename-base="asset?.name || 'transcript'"
      :words="transcriptWords"
      :word-level="!!asset?.transcriptWordLevel"
      :source="asset?.transcriptSource || ''"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useClientAssetsStore } from '@/stores/clientAssetsStore'
import VideoPlayer from '@/components/common/VideoPlayer.vue'
import TranscriptDrawer from '@/components/assets/TranscriptDrawer.vue'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/js/firebase'
import { logger as loggerService } from '@/services/loggerService'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  asset: {
    type: Object,
    default: null
  },
  showNavigation: {
    type: Boolean,
    default: false
  },
  canNavigatePrev: {
    type: Boolean,
    default: false
  },
  canNavigateNext: {
    type: Boolean,
    default: false
  },
  showSelectButton: {
    type: Boolean,
    default: false
  },
  isSelected: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'navigate', 'select'])

const clientAssetsStore = useClientAssetsStore()
const videoPlayer = ref(null)
const loading = ref(false)
const error = ref(null)
const videoUrl = ref('')
const assetUrl = ref('')
const posterUrl = ref('')
const isPlaying = ref(false)
const showTranscript = ref(false)
const transcriptWords = ref([])

const hasTranscript = computed(() => !!props.asset?.transcript)

// Word-level timings live in a separate doc — only fetched when the drawer opens.
watch(showTranscript, async (open) => {
  if (!open) {
    transcriptWords.value = []
    return
  }
  if (!props.asset?.transcriptWordLevel || !props.asset?.id) return
  try {
    const snap = await getDoc(doc(db, 'assetTranscripts', props.asset.id))
    transcriptWords.value = snap.exists() ? (snap.data().words || []) : []
  } catch (err) {
    loggerService.warn('VideoPreviewModal', 'Failed to load word-level transcript', err)
    transcriptWords.value = []
  }
})

const isVideo = computed(() => {
  if (!props.asset) return false
  const mime = props.asset.type || props.asset.contentType || props.asset.mimeType || ''
  return mime.startsWith('video/')
})

// canNavigate computed removed - navigation handled via individual canNavigatePrev/canNavigateNext props

watch(() => props.show, async (newVal) => {
  if (newVal && props.asset) {
    await loadAsset()
  } else if (!newVal) {
    cleanup()
  }
})

watch(() => props.asset, async (newAsset) => {
  // Prev/Next navigation: never carry the old asset's transcript over.
  showTranscript.value = false
  transcriptWords.value = []
  if (props.show && newAsset) {
    await loadAsset()
  }
})

async function loadAsset() {
  if (!props.asset) return
  
  loading.value = true
  error.value = null
  
  try {
    loggerService.info('VideoPreviewModal', `Loading asset: ${props.asset.name}`)

    const [url, thumbnail] = await Promise.all([
      clientAssetsStore.getAssetUrl(props.asset),
      clientAssetsStore.getThumbnailUrl(props.asset)
    ])

    loggerService.info('VideoPreviewModal', `Asset URL: ${url}`)
    loggerService.info('VideoPreviewModal', `Thumbnail URL: ${thumbnail}`)

    if (isVideo.value) {
      videoUrl.value = url
      posterUrl.value = thumbnail
    } else {
      assetUrl.value = url
    }

    await nextTick()

    loading.value = false

    // Delay autoplay to ensure CORS headers are loaded (video only)
    if (isVideo.value && videoPlayer.value) {
      setTimeout(() => {
        try {
          videoPlayer.value.play()
        } catch (err) {
          loggerService.warn('VideoPreviewModal', 'Autoplay failed', err)
        }
      }, 100)
    }
  } catch (err) {
    loggerService.error('VideoPreviewModal', 'Error loading asset', err)
    error.value = 'Failed to load asset'
    loading.value = false
  }
}

function cleanup() {
  if (videoPlayer.value) {
    try {
      videoPlayer.value.pause()
    } catch (err) {
      loggerService.warn('VideoPreviewModal', 'Error pausing video', err)
    }
  }
  
  showTranscript.value = false
  videoUrl.value = ''
  assetUrl.value = ''
  posterUrl.value = ''
  isPlaying.value = false
  error.value = null
}

function closeModal() {
  cleanup()
  emit('close')
}

function handleVideoError(event) {
  const videoElement = event?.target

  // cleanup() clears videoUrl, which makes the browser fire a bogus error event
  // (PIPELINE_ERROR_READ) on the torn-down element. Not a real playback failure.
  if (!videoElement?.src) return

  const errorDetails = {
    error: event?.target?.error,
    errorCode: event?.target?.error?.code,
    errorMessage: event?.target?.error?.message,
    src: videoElement?.src,
    readyState: videoElement?.readyState,
    networkState: videoElement?.networkState,
    crossOrigin: videoElement?.crossOrigin
  }
  
  loggerService.error('VideoPreviewModal', 'Video playback error', errorDetails)
  
  // Check if it's a CORS error
  if (videoElement?.error?.code === 2 || videoElement?.networkState === 3) {
    error.value = 'Unable to load video. This may be a temporary issue. Please try again.'
  } else {
    error.value = 'Error playing video'
  }
}

function onVideoPlay() {
  isPlaying.value = true
}

function onVideoPause() {
  isPlaying.value = false
}

function handleKeydown(event) {
  if (!props.show) return
  // Drawer on top owns the keyboard (Escape, space in the textarea).
  if (showTranscript.value) return

  switch (event.key) {
    case 'Escape':
      closeModal()
      break
    case ' ':
    case 'Spacebar':
      if (isVideo.value && videoPlayer.value) {
        event.preventDefault()
        if (isPlaying.value) {
          videoPlayer.value.pause()
        } else {
          videoPlayer.value.play()
        }
      }
      break
    case 'ArrowLeft':
      if (props.showNavigation && props.canNavigatePrev) {
        event.preventDefault()
        emit('navigate', -1)
      }
      break
    case 'ArrowRight':
      if (props.showNavigation && props.canNavigateNext) {
        event.preventDefault()
        emit('navigate', 1)
      }
      break
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  cleanup()
})
</script>

<style lang="scss" scoped>
.video-preview-modal {
  z-index: 100;
  
  .modal-card {
    width: 90vw;
    max-width: 1024px;
    
    @media screen and (max-width: 768px) {
      width: 100vw;
      height: 100vh;
      max-width: 100vw;
      margin: 0;
    }
  }
  
  .modal-card-body {
    padding: 1.5rem;
    
    @media screen and (max-width: 768px) {
      padding: 1rem;
    }
  }
  
  .video-preview-player {
    width: 100%;
    max-height: 70vh;
    object-fit: contain;
  }

  .image-preview {
    width: 100%;
    max-height: 70vh;
    object-fit: contain;
    display: block;
    margin: 0 auto;
  }
  
  .modal-card-foot {
    padding: 1rem 1.5rem;
    
    .level {
      width: 100%;
    }

    .level-item {
      gap: 0.5rem;
    }
  }
}
</style>